"use client";

import { useState, useRef, useEffect } from "react";

interface GalleryImage {
  src: string;
  alt: string;
  label: string;
}

const galleryImages: GalleryImage[] = [
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd9adf25-1769818656902.png",
  alt: "Freshly detailed black luxury sedan with mirror-like paint finish after full signature service",
  label: "Full Signature Detail"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_11e5ce706-1773134664148.png",
  alt: "Close-up of sparkling clean car wheel and tire after professional exterior detailing",
  label: "Wheel & Tire Detail"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_17d40ef96-1772903087634.png",
  alt: "Immaculate clean car interior with vacuumed seats and spotless dashboard after interior restoration",
  label: "Interior Restoration"
},
{
  src: "https://images.unsplash.com/photo-1611946206053-b6f16166c900",
  alt: "White sports car with glossy waxed exterior reflecting sunlight after exterior pro service",
  label: "Exterior Pro"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ba502751-1767880286911.png",
  alt: "Detailer hand washing a silver SUV with foam and microfiber cloth in San Diego driveway",
  label: "Mobile Service"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1278c40ef-1764691094283.png",
  alt: "Spotless car engine bay cleaned and degreased showing professional detailing attention to detail",
  label: "Full Essential Detail"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce3b7afb-1772067200530.png",
  alt: "Professional detailer applying ceramic coating to a red sports car for long-lasting paint protection",
  label: "Ceramic Coating"
},
{
  src: "https://images.unsplash.com/photo-1654522659498-97f749d7dd13",
  alt: "Luxury black SUV freshly washed and polished parked in a clean garage after full detail",
  label: "SUV Full Detail"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1e73f1b3e-1764734283334.png",
  alt: "Car headlights restored to crystal clear condition after professional headlight restoration service",
  label: "Headlight Restoration"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecb5ef5c-1772167474412.png",
  alt: "Detailer using a dual-action polisher to remove swirl marks and scratches from a dark blue car",
  label: "Paint Correction"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_160e630c0-1772186316091.png",
  alt: "Spotless black car interior with leather seats conditioned and dashboard cleaned to a shine",
  label: "Leather Conditioning"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ccc457a9-1772225344520.png",
  alt: "Silver sedan gleaming under sunlight after a complete exterior wash, clay bar, and wax treatment",
  label: "Exterior Wax & Shine"
}];

const PHOTOS_PER_PAGE = 6;
const ANIM_MS = 360;

export default function Gallery() {
  const [currentPage, setCurrentPage] = useState(0);
  const [animState, setAnimState] = useState<"idle" | "prep" | "run">("idle");
  const [slideDirection, setSlideDirection] = useState<"left" | "right">("left");
  const [incomingPage, setIncomingPage] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const animLock = useRef(false);

  const totalPages = Math.ceil(galleryImages.length / PHOTOS_PER_PAGE);

  const getImages = (page: number) =>
  galleryImages.slice(page * PHOTOS_PER_PAGE, page * PHOTOS_PER_PAGE + PHOTOS_PER_PAGE);

  // Preload next/prev page images for instant display
  useEffect(() => {
    const nextPage = currentPage === totalPages - 1 ? 0 : currentPage + 1;
    const prevPage = currentPage === 0 ? totalPages - 1 : currentPage - 1;
    [nextPage, prevPage].forEach((page) => {
      getImages(page).forEach((img) => {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.as = "image";
        link.href = img.src;
        document.head.appendChild(link);
      });
    });
  }, [currentPage]);

  const goToPage = (nextPage: number, direction: "left" | "right") => {
    if (animLock.current || nextPage === currentPage) return;
    animLock.current = true;
    setSlideDirection(direction);
    setIncomingPage(nextPage);
    // Step 1: mount incoming off-screen (no transition yet)
    setAnimState("prep");
    // Step 2: one frame later, trigger the transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAnimState("run");
        setTimeout(() => {
          setCurrentPage(nextPage);
          setIncomingPage(null);
          setAnimState("idle");
          animLock.current = false;
        }, ANIM_MS);
      });
    });
  };

  const handlePrev = () => {
    const prev = currentPage === 0 ? totalPages - 1 : currentPage - 1;
    goToPage(prev, "right");
  };

  const handleNext = () => {
    const next = currentPage === totalPages - 1 ? 0 : currentPage + 1;
    goToPage(next, "left");
  };

  const handleDotClick = (i: number) => {
    if (i === currentPage) return;
    goToPage(i, i > currentPage ? "left" : "right");
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      diff > 0 ? handleNext() : handlePrev();
    }
    touchStartX.current = null;
  };

  const isAnimating = animState === "prep" || animState === "run";
  const transition = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity ${ANIM_MS}ms ease`;

  // Outgoing: starts at 0, slides out when animState === "run"
  const outgoingStyle: React.CSSProperties = {
    willChange: "transform, opacity",
    transform: animState === "run" ?
    slideDirection === "left" ? "translateX(-105%)" : "translateX(105%)" :
    "translateX(0%)",
    opacity: animState === "run" ? 0.2 : 1,
    transition: animState === "run" ? transition : "none"
  };

  // Incoming: starts off-screen (prep), slides to center (run)
  const incomingStyle: React.CSSProperties = {
    willChange: "transform, opacity",
    transform: animState === "run" ? "translateX(0%)" :
    slideDirection === "left" ? "translateX(105%)" : "translateX(-105%)",
    opacity: animState === "run" ? 1 : 0,
    transition: animState === "run" ? transition : "none"
  };

  const renderGrid = (page: number, style: React.CSSProperties, isIncoming: boolean) =>
  <div
    className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4"
    style={{
      position: isIncoming ? "absolute" : "relative",
      top: isIncoming ? 0 : undefined,
      left: isIncoming ? 0 : undefined,
      width: "100%",
      ...style
    }}>
    
      {getImages(page).map((image, index) =>
    <div
      key={`${page}-${index}`}
      className="relative group overflow-hidden rounded-xl border border-silver-DEFAULT/10 hover:border-accent/40 transition-all">
      
          <div className="aspect-square bg-white/5 overflow-hidden">
            <img
          src={image.src}
          alt={image.alt}
          loading={isIncoming ? "eager" : index < 6 ? "eager" : "lazy"}
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          style={{ willChange: "transform" }} />
        
          </div>
        </div>
    )}
    </div>;


  return (
    <section id="gallery" className="py-20 sm:py-28 bg-[#050a14]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-accent font-semibold uppercase tracking-widest text-[17px]">OUR ART</span>
          <h2 className="text-4xl sm:text-5xl font-black text-white mt-3 mb-4">Gallery</h2>
          <p className="text-silver-light max-w-xl mx-auto">See the difference a professional detail makes. 
Every car leaves looking its best.
          </p>
        </div>

        {/* Carousel Wrapper */}
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={handlePrev}
            aria-label="Previous photos"
            className="absolute -left-4 sm:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-black/70 border border-silver-DEFAULT/20 hover:border-accent/60 hover:bg-accent/20 text-white transition-all shadow-lg active:scale-90">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Sliding Gallery Grid */}
          <div
            className="overflow-hidden"
            style={{ position: "relative" }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}>
            
            {/* Outgoing grid */}
            {renderGrid(currentPage, outgoingStyle, false)}

            {/* Incoming grid — overlaid, slides in simultaneously */}
            {isAnimating && incomingPage !== null &&
            renderGrid(incomingPage, incomingStyle, true)
            }
          </div>

          {/* Right Arrow */}
          <button
            onClick={handleNext}
            aria-label="Next photos"
            className="absolute -right-4 sm:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-black/70 border border-silver-DEFAULT/20 hover:border-accent/60 hover:bg-accent/20 text-white transition-all shadow-lg active:scale-90">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Page Indicators */}
        <div className="flex justify-center items-center gap-2 mt-8">
          {Array.from({ length: totalPages }).map((_, i) =>
          <button
            key={i}
            onClick={() => handleDotClick(i)}
            aria-label={`Go to page ${i + 1}`}
            className={`rounded-full transition-all duration-300 ${
            i === currentPage ?
            "bg-accent w-6 h-2.5 scale-100" : "bg-silver-DEFAULT/30 hover:bg-silver-DEFAULT/60 w-2.5 h-2.5"}`
            } />

          )}
          <span className="ml-3 text-silver-light text-xs">
            {currentPage + 1} / {totalPages}
          </span>
        </div>

        {/* Social CTA */}
        <div className="text-center mt-10">
          <p className="font-extrabold tracking-wide mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent text-[25px]">
            Follow Us!
          </p>
          <p className="mb-6 text-white text-[15px]">Follow us to stay updated on content and special deals</p>
          <div className="flex flex-wrap justify-center gap-4">
            {/* Instagram */}
            <a
              href="https://www.instagram.com/figure8detailing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white font-bold px-6 py-3 rounded-full text-sm transition-all hover:scale-105 shadow-lg hover:shadow-pink-500/40">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              Instagram
            </a>
            {/* TikTok */}
            <a
              href="https://www.tiktok.com/@figure8detailing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-black hover:bg-neutral-900 border border-[#69C9D0] text-white font-bold px-6 py-3 rounded-full text-sm transition-all hover:scale-105 shadow-lg hover:shadow-[#69C9D0]/40">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
              </svg>
              TikTok
            </a>
            {/* YouTube */}
            <a
              href="https://www.youtube.com/@figure8detailing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF0000] hover:bg-red-700 text-white font-bold px-6 py-3 rounded-full text-sm transition-all hover:scale-105 shadow-lg hover:shadow-red-500/40">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              YouTube
            </a>
          </div>
        </div>
      </div>
    </section>);

}